// Statuses that mark a case as Non-current — shared across asset, liability, and policy.
// Any status NOT in this list (including blank/null) is treated as Current.
// Comparison is case- and separator-insensitive via normalizeStatus.
export const NON_CURRENT_STATUSES: string[] = [
  "Cancelled",
  "Declined",
  "Matured",
  "Lapsed",
  "Terminated",
  "Surrendered",
  "Transferred",
  "Redeemed",
  "Re-paid",
  "Not Proceeded With",
  "Expired",
  "Converted",
  "Not Taken Up",
  "Out Of Force",
];

// Normalises a case_type string for comparison: lowercased, hyphens and
// underscores replaced with spaces, and trimmed.  Keeps the filter resilient
// to DB values like "Unit-linked", "unit_linked", or "Unit Linked" all
// matching the same route segment.
export const normalizeCaseType = (s: string): string =>
  (s ?? "").toLowerCase().replace(/[-_]/g, " ").trim();

// Normalises a status string for comparison: same rules as normalizeCaseType.
// Lets "CANCELLED", "cancelled", "Cancelled", "NOT_TAKEN_UP", "not-taken-up"
// all match the canonical values in NON_CURRENT_STATUSES.
export const normalizeStatus = (s: string): string =>
  (s ?? "").toLowerCase().replace(/[-_]/g, " ").trim();

// Returns true when the status is NOT a known non-current status.
// Empty/null statuses are treated as current.
// The `module` param is kept for API compatibility — the denylist is now
// shared across asset, liability, and policy.
export const isCurrentStatus = (
  _module: "asset" | "liability" | "policy",
  status: string
): boolean => {
  const n = normalizeStatus(status);
  return !NON_CURRENT_STATUSES.some((s) => normalizeStatus(s) === n);
};

// Extracts the correct status string from a case item based on its case_type.
// Asset records return ALL status columns (investment_status, share_status,
// bank_status) for every row, so ?? chaining would pick the wrong field.
// Instead, case_type is used to select the authoritative column:
//   Unit-linked       → investment_status
//   Shares            → share_status
//   Banks/B Societies → bank_status
//   Home/Personal     → (no status field, always "" => always Current)
//   Liability/Policy  → status
export const getItemStatus = (item: any): string => {
  const n = normalizeCaseType(item?.case_type ?? "");
  if (n === "unit linked") return item?.investment_status ?? "";
  if (n === "shares") return item?.share_status ?? "";
  if (n === "banks/b societies") return item?.bank_status ?? "";
  // Home/Personal genuinely has no status column: ASSET_HAS_STATUS_FIELD marks
  // "home-personal" false, and its table column set carries no Status column.
  // Returned explicitly instead of falling through to `status` so that a stray
  // `status` value on the row can never mark a Home/Personal asset Non-current
  // -- the same bug class the case_type switch above exists to prevent.
  if (n === "home/personal") return "";
  // Liability and Policy rows carry a single generic `status` field.
  return item?.status ?? "";
};

// Legacy — kept so any remaining callers don't break during migration.
// No longer used by the main fetch flow (status filtering is now client-side
// via isCurrentStatus / NON_CURRENT_STATUSES).
export const getCurrentStatusParam = (): string => "";
